<template>
  <section>
    <transition name=fade>
      <div v-show="showCommonSearches" class="resource-filters-module has-mb-24">
        <h3 class="title is-3">Common Searches</h3>
        <ul class="resource-filters__wrapper">
          <li
            v-for="filter in featuredFilters"
            :key="filter.label"
            class="resource-filters__item"
          >
            <a
              :href="filter.filter"
              class="resource-filters__link"
              @click.prevent="applyFeaturedFilter(filter.filter)"
            >
              {{ filter.label }}
            </a>
          </li>
        </ul>
      </div>
    </transition>
    <autocomplete-search
      label-id="resource-input"
      placeholder="Search resources or filters."
      :filters="allFilters"
      :names="names"
      @filter-unique="filterUniqueResource"
      @search="updateSearch"
      @update-filter-selection="updateFilterSelection"
    ></autocomplete-search>
    <resource-filter
      :topics="topics"
      :audiences="audiences"
      :resultCount="totalResources"
      :searchTerm="searchTerm"
      @update-filter="updateFilters"
    ></resource-filter>
    <div
      class="resources"
      :class="{ 'slide': slide }"
      @transitionend="manageTransition()"
    >
      <resource
        v-for="resource in resources"
        :key="resource.id"
        :badges="resource.badges"
        :lastReviewed="resource.last_reviewed"
        :location="resource.location"
        :organisation="resource.organisation"
        :ratingLink="ratingLink"
        :score="resource.score"
        :summary="resource.summary"
        :tags="resource.tags"
        :websiteTitle="resource.website_title"
        :websiteUrl="resource.website_url"
        @search="updateSearch"
      ></resource>
    </div>
    <div class="resources__page_controls">
      <button v-if="prevDisabled" class="button resources__previous" disabled>
        <span class="icon icon--arrow"><arrow-svg></arrow-svg></span>
        <span class="resources__button-text">Previous</span>
      </button>
      <a
        v-else
        :href="pageLink(-1)"
        class="button resources__previous"
        rel="prev"
        @click.prevent="getPage(-1, $event)"
      >
        <span class="icon icon--arrow"><arrow-svg></arrow-svg></span>
        <span class="resources__button-text">Previous</span>
      </a>
      <p class="is-small resources__pages">Page {{ page + 1 }} of {{ pageCount }}</p>
      <button v-if="nextDisabled" class="button resources__next" disabled>
        <span class="resources__button-text">Next</span>
        <span class="icon icon--arrow"><arrow-svg></arrow-svg></span>
      </button>
      <a
        v-else
        :href="pageLink(1)"
        class="button resources__next"
        rel="next"
        @click.prevent="getPage(1, $event)"
      >
        <span class="resources__button-text">Next</span>
        <span class="icon icon--arrow"><arrow-svg></arrow-svg></span>
      </a>
    </div>
  </section>
</template>

<script>
import Vue from 'vue';
import axios from 'axios';
import { sendGaEvent } from '@/utils';
import AutocompleteSearch from '../AutocompleteSearch.vue';
import Resource from './Resource.vue';
import ResourceFilter from './ResourceFilter.vue';
import ArrowSvg from '../svg/arrow.vue';

const PAGE_TOTAL = 20;

export default {
  name: 'resource-index',
  components: {
    AutocompleteSearch,
    Resource,
    ResourceFilter,
    ArrowSvg,
  },
  props: {
    allFilters: Array,
    audiences: Object,
    featuredFilters: Array,
    names: Array,
    ratingLink: String,
    topics: Object,
  },
  computed: {
    pageCount() {
      return Math.ceil(this.totalResources / PAGE_TOTAL);
    },
    prevDisabled() {
      return this.page === 0;
    },
    nextDisabled() {
      return this.page + 1 >= this.pageCount;
    },
    selectedTopic: {
      get() {
        return this.$store.state.selectedTopic;
      },
      set(value) {
        this.$store.commit('setSelectedTopic', value);
      },
    },
    selectedAudience: {
      get() {
        return this.$store.state.selectedAudience;
      },
      set(value) {
        this.$store.commit('setSelectedAudience', value);
      },
    },
    searchTerm: {
      get() {
        return this.$store.state.searchTerm;
      },
      set(value) {
        this.$store.commit('setSearchTerm', value);
      },
    },
  },
  data() {
    return {
      page: 0,
      resources: [],
      slide: true,
      slug: null,
      totalResources: 0,
      showCommonSearches: false,
    };
  },
  mounted() {
    // Set event handler to call function when history changes
    window.onpopstate = this.updateFromUrl;
    this.updateFromUrl();
    this.showFeatured();
  },
  methods: {
    /**
     * update filters from featured filter params
     */
    applyFeaturedFilter(filter) {
      const params = new URLSearchParams(filter);

      const topic = Number.parseInt(params.get('topic'), 10);
      if (topic) {
        this.selectedTopic = topic;
      }
      const audience = Number.parseInt(params.get('audience'), 10);
      if (audience) {
        this.selectedAudience = audience;
      }
      this.updateUrl();
      this.getData();
      this.showFeatured();
    },
    /**
     * Contruct the query string for the API from selected filters/pages/slug
     */
    buildQueryString() {
      let queryString;
      if (this.slug) {
        queryString = `slug=${this.slug}`;
      } else {
        queryString = [];
        if (this.searchTerm) {
          queryString.push(`search=${this.searchTerm}`);
        }
        if (this.selectedTopic) {
          queryString.push(`topic=${this.selectedTopic}`);
        }
        if (this.selectedAudience) {
          queryString.push(`audience=${this.selectedAudience}`);
        }
        queryString.push(`limit=${PAGE_TOTAL}&offset=${this.page * PAGE_TOTAL}`);
        queryString = queryString.join('&');
      }
      return `${process.env.VUE_APP_API_URL}resources/?${queryString}`;
    },
    /**
     * Filter for a resource with the provided slug
     */
    filterUniqueResource(payload) {
      this.slug = payload;
      this.selectedTopic = '';
      this.selectedAudience = '';
      this.resetPage();
      this.updateUrl();
      this.getData();
      this.showFeatured();
    },
    /**
     * Return paginated resources, with applied filters
     */
    getData() {
      // If transition out hasn't started
      // Early exit and wait for @transitionend
      if (!this.slide) {
        this.slide = true;
        return;
      }

      axios.get(this.buildQueryString()).then((response) => {
        this.resources = response.data.items;
        this.totalResources = response.data.meta.total_count;
        Vue.nextTick(() => {
          this.slide = false;
          this.slug = null;
        });
      });
    },
    /**
     * update page and reload data
     */
    getPage(increment, event) {
      this.page += increment;
      this.updateUrl();
      event.currentTarget.blur();
      window.scrollTo(0, 0);
      this.getData();
    },
    /**
     * Hide or show common searches
     */
    showFeatured() {
      if (
        this.featuredFilters.length > 0
        && this.$store.state.searchTerm === ''
        && this.$store.state.selectedTopic === ''
        && this.$store.state.selectedAudience === ''
      ) {
        this.showCommonSearches = true;
      } else {
        this.showCommonSearches = false;
      }
    },
    /**
     * Fetch new data on transition end only if transitioning out
     */
    manageTransition() {
      if (this.slide) {
        this.getData();
      }
    },
    /**
     * Construct dummy page link for crawling
     */
    pageLink(increment) {
      let newPage;
      if (increment < 0) {
        newPage = this.page;
      } else {
        newPage = this.page + 2;
      }

      const queryString = [];

      if (this.searchTerm) {
        queryString.push(`search=${this.searchTerm}`);
      }
      if (this.selectedTopic) {
        queryString.push(`topic=${this.selectedTopic}`);
      }
      if (this.selectedAudience) {
        queryString.push(`audience=${this.selectedAudience}`);
      }
      if (newPage > 1) {
        queryString.push(`page=${this.page + 1 + increment}`);
      }
      if (queryString.length === 0) {
        return window.location.pathname;
      }
      return `?${queryString.join('&')}`;
    },
    /**
     * Reset page number
     */
    resetPage() {
      this.page = 0;
    },
    /**
     * update selected filter, also updating the dropdown in ResourceFilter
     */
    updateFilterSelection(payload) {
      const { field, value } = payload;

      if (field === 'topic') {
        this.selectedTopic = value;
      }
      if (field === 'audience') {
        this.selectedAudience = value;
      }
      this.updateFilters(payload);
    },
    /**
     * update selected filters
     */
    updateFilters() {
      this.resetPage();
      this.updateUrl();
      this.getData();
      this.showFeatured();
    },
    /**
     * Update data from URL params
     */
    updateFromUrl() {
      const urlParams = new URLSearchParams(window.location.search);
      this.slug = urlParams.get('slug');

      const search = urlParams.get('search');
      if (search) {
        this.searchTerm = search;
      } else {
        this.searchTerm = '';
      }
      const topic = Number.parseInt(urlParams.get('topic'), 10);
      const audience = Number.parseInt(urlParams.get('audience'), 10);
      if (topic) {
        this.selectedTopic = topic;
      } else {
        this.selectedTopic = '';
      }
      if (audience) {
        this.selectedAudience = audience;
      } else {
        this.selectedAudience = '';
      }


      const urlPage = Number.parseInt(urlParams.get('page'), 10);
      if (urlPage && urlPage > 0) {
        this.page = urlPage - 1;
      } else {
        this.page = 0;
      }
      this.getData();
    },
    /**
     * update searchTerm when someone types in search bar and search again.
     */
    updateSearch() {
      this.resetPage();
      this.updateUrl();
      this.getData();
      this.showFeatured();
      if (this.searchTerm) { // Don't send event with empty string
        sendGaEvent('Search', 'druginfo', this.searchTerm);
      }
    },
    /**
     * Update the url with the current state of data, avoiding ? if no params are set
     */
    updateUrl() {
      const urlParams = new URLSearchParams(window.location.search);
      if (this.slug) {
        urlParams.set('slug', this.slug);
      } else {
        urlParams.delete('slug');
      }
      if (this.searchTerm) {
        urlParams.set('search', this.searchTerm);
      } else {
        urlParams.delete('search');
      }
      if (this.selectedTopic) {
        urlParams.set('topic', this.selectedTopic);
      } else {
        urlParams.delete('topic');
      }
      if (this.selectedAudience) {
        urlParams.set('audience', this.selectedAudience);
      } else {
        urlParams.delete('audience');
      }
      if (this.page > 0) {
        urlParams.set('page', this.page + 1);
      } else {
        urlParams.delete('page');
      }
      const queryString = urlParams.toString();
      if (queryString) {
        window.history.pushState({}, '', `?${queryString}`);
      } else {
        window.history.pushState({}, '', window.location.pathname);
      }
    },
  },
};
</script>
